import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Check, ChevronRight, ChevronLeft, AlertTriangle, Loader2, Send, X, Copy, Users2, Trash2, ExternalLink, Paperclip, Pencil, Undo2 } from 'lucide-react';
import { api } from '../../lib/api';
import { formatBytes } from '../../lib/communications';
import { useAuth } from '../../context/AuthContext';
import BackLink from '../../components/admin/BackLink';
import AccountPicker from '../../components/admin/AccountPicker';
import TemplateEditor from '../../components/admin/TemplateEditor';
import TemplatePicker from '../../components/admin/TemplatePicker';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import Toast, { useToast } from '../../components/admin/Toast';
import { FromPicker } from '../../components/admin/EmailComposer';
import { wrapHtml } from '../../components/admin/ConversationThread';
import { CONTACT_ROLE_OPTIONS, contactRoleLabel } from '../../lib/outreach';
import { groupSenders, findSenderOption, defaultFromEmail, firstName, localPart, shortName, messageTime } from '../../lib/communications';
import {
  STAGES, BOROUGHS, PURPOSE_COPY, skipReasonLabel, audienceSummary, audienceHasCriteria,
  broadcastStatusMeta, RECIPIENT_STATUS_META,
} from '../../lib/broadcasts';

// /admin/communications/broadcasts/:id — Audience → Message → Preview each
// school → Send. Drafts autosave as you go; once sent, the same page is the
// report (who got it, who was skipped and why, what failed).

const STEP_LABELS = ['Audience', 'Message', 'Preview each school', 'Send'];
const BACK = '/admin/communications?tab=broadcasts';
const inputCls = 'w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-[13px] text-white placeholder-gray-500 focus:outline-none focus:border-[#B0FC38]/40';
const primaryBtn = 'inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed';
const ghostBtn = 'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] text-gray-400 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed';
const chip = (on) => `px-2.5 py-1 rounded-full text-[11px] border transition-colors ${on ? 'bg-[#B0FC38]/15 border-[#B0FC38]/40 text-white' : 'border-white/[0.08] text-gray-400 hover:text-white'}`;
const POLL_MS = 2500;

const PATCH_KEYS = {
  name: 'name', purpose: 'purpose', fromEmail: 'from_email', fromName: 'from_name', mailboxId: 'mailbox_id', audience: 'audience',
  subjectTemplate: 'subject_template', bodyTemplateHtml: 'body_template_html', bodyTemplateText: 'body_template_text', templateId: 'template_id',
  cc: 'cc', bcc: 'bcc',
};

export default function BroadcastEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [toast, setToast] = useToast();
  const [b, setB] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [step, setStep] = useState(0);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [senders, setSenders] = useState(null);
  const [summary, setSummary] = useState(null);      // preview without bodies (audience step)
  const [rendered, setRendered] = useState(null);    // preview with bodies (preview step)
  const [selectedId, setSelectedId] = useState(null);
  const [sendError, setSendError] = useState(null);
  const [sending, setSending] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);
  const [pendingCancel, setPendingCancel] = useState(false);
  const pendingRef = useRef({});
  const timerRef = useRef(null);
  const flushingRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await api.get(`/api/admin/communications/broadcasts/${id}`);
      setB(d.broadcast); setRecipients(d.recipients || []); setAttachments(d.attachments || []); setProgress(d.progress || null);
      setLoadError('');
    } catch (err) { setLoadError(err?.data?.error || err?.message || 'Failed to load this broadcast'); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let alive = true;
    const p = api.get('/api/admin/communications/senders');
    if (!p || typeof p.then !== 'function') return undefined;
    p.then((d) => { if (alive) setSenders(d?.senders || []); }).catch(() => { if (alive) setSenders([]); });
    return () => { alive = false; };
  }, []);
  const groups = useMemo(() => groupSenders(senders || []), [senders]);
  const fromOption = useMemo(() => findSenderOption(groups, b?.fromEmail, b?.mailboxId), [groups, b?.fromEmail, b?.mailboxId]);

  // ---- autosave -------------------------------------------------------------
  // Saves are strictly serialized: one PATCH in flight at a time, each carrying
  // everything queued since the last one, so an older save can never land
  // after — and overwrite — a newer one.
  const flush = useCallback(async () => {
    if (flushingRef.current) return flushingRef.current;
    const pending = pendingRef.current;
    pendingRef.current = {};
    if (!Object.keys(pending).length) return true;
    setSaveState('saving');
    const body = {};
    for (const [k, v] of Object.entries(pending)) body[PATCH_KEYS[k]] = v;
    const run = api.patch(`/api/admin/communications/broadcasts/${id}`, body)
      .then((d) => { if (d?.broadcast?.audience) setB((prev) => (prev ? { ...prev, audience: d.broadcast.audience } : prev)); setSaveState('saved'); return true; })
      .catch((err) => {
        // Keep the unsaved changes queued so the next attempt retries them.
        pendingRef.current = { ...pending, ...pendingRef.current };
        setSaveState('error');
        setToast({ message: 'Changes not saved', detail: err?.data?.error || err?.message });
        return false;
      })
      .finally(() => { flushingRef.current = null; });
    flushingRef.current = run;
    const ok = await run;
    // Anything typed while that request was out goes in the next one.
    if (ok && Object.keys(pendingRef.current).length) return flush();
    return ok;
  }, [id, setToast]);

  const save = useCallback((patch) => {
    setB((prev) => (prev ? { ...prev, ...patch } : prev));
    pendingRef.current = { ...pendingRef.current, ...patch };
    setSaveState('saving');
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, 600);
  }, [flush]);
  useEffect(() => () => clearTimeout(timerRef.current), []);
  // Resolves true only when everything typed so far is on the server. Nothing
  // that sends (or moves toward sending) proceeds on a failed save.
  const settle = useCallback(async () => {
    clearTimeout(timerRef.current);
    if (flushingRef.current) { const prev = await flushingRef.current; if (prev === false) return false; }
    const ok = await flush();
    return ok !== false;
  }, [flush]);

  // Default From once senders arrive.
  useEffect(() => {
    if (!b || b.status !== 'draft' || !senders || b.fromEmail) return;
    const def = defaultFromEmail(groups);
    const opt = def ? findSenderOption(groups, def) : null;
    if (def) save({ fromEmail: def, fromName: opt?.name || null, mailboxId: opt?.mailboxId || null });
  }, [b, senders, groups, save]);

  // ---- audience summary (step 0) ----------------------------------------------
  const audienceKey = JSON.stringify(b?.audience || null);
  useEffect(() => {
    if (!b || b.status !== 'draft' || step !== 0) return undefined;
    let alive = true;
    const t = setTimeout(async () => {
      await settle();
      try {
        const d = await api.get(`/api/admin/communications/broadcasts/${id}/preview?limit=500`);
        if (alive) setSummary(d);
      } catch { /* keep the previous summary */ }
    }, 350);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceKey, step, id, b?.status]);
  // A school removed from the list leaves the counts at once, before the
  // server's recount lands.
  const shownSummary = useMemo(() => withRemovals(summary, b?.audience), [summary, b?.audience]);
  const readyIds = useMemo(() => new Set((shownSummary?.recipients || []).filter((r) => r.status === 'ready').map((r) => r.accountId)), [shownSummary]);

  // ---- rendered preview (step 2) -------------------------------------------------
  useEffect(() => {
    if (!b || b.status !== 'draft' || step !== 2) return undefined;
    let alive = true;
    setRendered(null);
    (async () => {
      await settle();
      try {
        const d = await api.get(`/api/admin/communications/broadcasts/${id}/preview?render=1&limit=200`);
        if (!alive) return;
        setRendered(d);
        setSelectedId((cur) => (cur && d.recipients.some((r) => r.accountId === cur && r.status === 'ready') ? cur : (d.recipients.find((r) => r.status === 'ready')?.accountId || null)));
      } catch (err) { if (alive) setRendered({ error: err?.data?.error || err?.message || 'Preview failed' }); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, id, b?.status]);

  // ---- sending ------------------------------------------------------------------
  const [preparing, setPreparing] = useState(false);
  const [preparingSince, setPreparingSince] = useState(null);
  const [preparingLong, setPreparingLong] = useState(false);
  const applySendResult = useCallback((d) => {
    if (d?.preparing) { setPreparing(true); setPreparingSince((t) => t || Date.now()); return; } // another request is freezing the audience; poll until it publishes
    setPreparing(false); setPreparingSince(null); setPreparingLong(false);
    if (d?.broadcast) setB(d.broadcast);
    if (d?.progress) setProgress(d.progress);
    if (d?.progress?.blocked) setSendError({ title: d.broadcast?.lastError || (d.progress.blocked === 'oauth_not_configured' ? 'Google sign-in isn’t configured on this environment.' : 'Sending is blocked right now.'), retryable: true });
  }, []);

  const sendChunk = useCallback(async () => {
    try {
      const d = await api.post(`/api/admin/communications/broadcasts/${id}/send`, {});
      applySendResult(d);
      return d;
    } catch (err) {
      setSendError({ title: err?.data?.error || err?.message || 'Sending failed', code: err?.data?.code, retryable: true });
      return null;
    }
  }, [id, applySendResult]);

  const startSend = async () => {
    setSending(true); setSendError(null);
    if (!(await settle())) { setSending(false); setSendError({ title: 'Your latest changes are not saved — nothing was sent. Fix the connection and try again.', retryable: true }); return; }
    const d = await sendChunk();
    setSending(false);
    if (d?.broadcast?.status === 'sending' || d?.broadcast?.status === 'sent') load();
  };

  // Someone else's request is preparing the audience: watch for it to publish.
  useEffect(() => {
    if (!preparing) return undefined;
    let alive = true;
    const t = setInterval(async () => {
      // After 45 s with no publish, the preparer has very likely died: offer a takeover.
      setPreparingLong(!!preparingSince && Date.now() - preparingSince > 45000);
      try {
        const r = await api.get(`/api/admin/communications/broadcasts/${id}`);
        if (!alive) return;
        if (r.broadcast?.status !== 'draft') { setPreparing(false); setPreparingSince(null); setPreparingLong(false); setB(r.broadcast); setRecipients(r.recipients || []); setProgress(r.progress || null); }
      } catch { /* next tick */ }
    }, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [preparing, preparingSince, id]);

  // While sending, keep nudging chunks and refreshing the report.
  const isSending = b?.status === 'sending';
  useEffect(() => {
    if (!isSending || sendError) return undefined;
    let alive = true;
    const t = setInterval(async () => {
      const d = await sendChunk();
      if (!alive) return;
      if (!d || d.broadcast?.status !== 'sending') load();
      else { try { const r = await api.get(`/api/admin/communications/broadcasts/${id}`); if (alive) { setRecipients(r.recipients || []); setProgress(r.progress || null); } } catch { /* next tick */ } }
    }, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [isSending, sendError, sendChunk, load, id]);

  const cancelSend = async () => {
    const d = await api.post(`/api/admin/communications/broadcasts/${id}/cancel`, {});
    setB(d.broadcast); setProgress(d.progress); load();
    setToast({ message: 'Broadcast cancelled', detail: 'Schools not yet emailed were recorded as skipped.' });
  };

  // Sent broadcasts accept a name-only PATCH (the server refuses anything else).
  const renameSent = async (name) => {
    try {
      const d = await api.patch(`/api/admin/communications/broadcasts/${id}`, { name });
      setB(d.broadcast);
    } catch (err) {
      setToast({ message: 'Couldn’t rename', detail: err?.data?.error || err?.message });
      throw err;
    }
  };

  const duplicate = async () => {
    try {
      const d = await api.post('/api/admin/communications/broadcasts', {
        name: `${b.name} (copy)`, purpose: b.purpose, from_email: b.fromEmail, from_name: b.fromName, audience: b.audience, cc: b.cc || [], bcc: b.bcc || [],
        subject_template: b.subjectTemplate, body_template_html: b.bodyTemplateHtml, body_template_text: b.bodyTemplateText, template_id: b.templateId, mailbox_id: b.mailboxId,
        copy_attachments_from: b.id,
      });
      navigate(`/admin/communications/broadcasts/${d.broadcast.id}`);
    } catch (err) { setToast({ message: 'Couldn’t duplicate', detail: err?.data?.error || err?.message }); }
  };

  // ---- attachments (drafts only) ---------------------------------------------
  const uploadFiles = async (files, accountId = null) => {
    if (!files || !files.length) return;
    const fd = new FormData();
    for (const f of files) fd.append('files', f);
    if (accountId) fd.append('account_id', accountId);
    try {
      const d = await api.post(`/api/admin/communications/broadcasts/${id}/attachments`, undefined, { body: fd });
      setAttachments(d.all || []);
      setToast({ message: `${files.length === 1 ? files[0].name : `${files.length} files`} attached${accountId ? ' for this school' : ' for every school'}` });
    } catch (err) {
      setToast({ message: 'Couldn’t attach', detail: err?.data?.error || err?.message });
    }
  };
  const removeAttachment = async (att) => {
    try {
      await api.delete(`/api/admin/communications/broadcasts/${id}/attachments/${att.id}`);
      setAttachments((list) => list.filter((a) => a.id !== att.id));
    } catch (err) {
      setToast({ message: 'Couldn’t remove the file', detail: err?.data?.error || err?.message });
    }
  };

  const removeDraft = async () => {
    await api.delete(`/api/admin/communications/broadcasts/${id}`);
    navigate(BACK);
  };

  const sendTest = async ({ subject, bodyHtml, bodyText, previewAccount }) => {
    save({ subjectTemplate: subject, bodyTemplateHtml: bodyHtml, bodyTemplateText: bodyText });
    if (!(await settle())) throw new Error('Your latest changes are not saved yet — the test would not match. Try again.');
    // The school shown in the preview, when this broadcast will email it;
    // else the one picked on the Preview step; else the first ready school.
    const previewed = previewAccount?.id && readyIds.has(previewAccount.id) ? previewAccount.id : null;
    const d = await api.post(`/api/admin/communications/broadcasts/${id}/test`, { account_id: previewed || selectedId || undefined });
    return { to: d.to, school: d.school };
  };

  // ---- render -------------------------------------------------------------------
  if (user?.role !== 'nexsav_admin' && user?.role !== 'ops') return null;
  if (loadError) return <div className="max-w-5xl mx-auto"><BackLink to={BACK} className="inline-flex items-center gap-1.5 text-[12px] text-gray-500 hover:text-white mb-4">Back to Broadcasts</BackLink><div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">{loadError}</div></div>;
  if (!b) return <div className="max-w-5xl mx-auto animate-pulse"><div className="h-8 w-64 rounded bg-white/[0.04] mb-6" /><div className="h-64 rounded-xl bg-white/[0.03]" /></div>;

  if (b.status !== 'draft') {
    return (
      <div className="max-w-5xl mx-auto" data-testid="broadcast-report">
        <BackLink to={BACK} className="inline-flex items-center gap-1.5 text-[12px] text-gray-500 hover:text-white mb-4">Back to Broadcasts</BackLink>
        <Report b={b} recipients={recipients} progress={progress} sendError={sendError} onRetry={() => { setSendError(null); }} onCancel={() => setPendingCancel(true)} onDuplicate={duplicate} onRename={renameSent}
          onDelete={b.status !== 'sending' && (user?.role === 'nexsav_admin' || b.sentCount === 0) ? () => setPendingDelete(true) : null} />
        <ConfirmDialog open={pendingCancel} title="Stop this broadcast?" description="Schools that haven’t been emailed yet will be recorded as skipped. Emails already sent stay sent." confirmLabel="Stop sending" destructive onConfirm={cancelSend} onClose={() => setPendingCancel(false)} />
        <ConfirmDialog open={pendingDelete} title={b.sentCount > 0 ? 'Remove this broadcast from the list?' : 'Delete this broadcast?'}
          description={b.sentCount > 0 ? `It reached ${b.sentCount} school${b.sentCount === 1 ? '' : 's'}. Those emails stay on each school’s Communications tab; only this report disappears from Broadcasts.` : 'Nothing was sent. The broadcast and its files are removed.'}
          confirmLabel={b.sentCount > 0 ? 'Remove from list' : 'Delete'} destructive onConfirm={removeDraft} onClose={() => setPendingDelete(false)} />
        <Toast toast={toast} onDismiss={() => setToast(null)} />
      </div>
    );
  }

  const aud = b.audience || {};
  const ready = shownSummary?.ready ?? null;
  const canContinueAudience = audienceHasCriteria(aud);
  const canContinueMessage = !!b.fromEmail && !!String(b.subjectTemplate || '').trim() && !!String(b.bodyTemplateHtml || '').replace(/<[^>]*>/g, '').trim();
  const saved = saveState !== 'error';
  const stepReady = [canContinueAudience && saved, canContinueMessage && saved, !!rendered?.canSend && saved, saved];

  return (
    <div className="max-w-6xl mx-auto" data-testid="broadcast-editor">
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <BackLink to={BACK} className="inline-flex items-center gap-1.5 text-[12px] text-gray-500 hover:text-white mb-2">Back to Broadcasts</BackLink>
          <label htmlFor="broadcast-name" className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">Broadcast name <span className="normal-case tracking-normal text-gray-600">· for your list and report, not sent to schools</span></label>
          <div className="relative max-w-xl">
            <input
              id="broadcast-name"
              value={b.name}
              onChange={(e) => save({ name: e.target.value })}
              onFocus={(e) => { if (isUntitled(e.target.value)) e.target.select(); }}
              placeholder="e.g. Fall check-in — District 24"
              data-testid="broadcast-name"
              className={`block w-full rounded-lg px-3 py-2 pr-9 text-lg md:text-xl font-semibold tracking-tight border focus:outline-none focus:border-[#B0FC38]/50 focus:bg-white/[0.04] ${isUntitled(b.name) ? 'text-gray-400 border-amber-500/40 bg-amber-500/[0.04]' : 'text-white border-white/[0.1] bg-white/[0.02] hover:border-white/[0.2]'}`}
            />
            <Pencil className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true" />
          </div>
          {isUntitled(b.name) && <p className="text-[11px] text-amber-300/90 mt-1" data-testid="name-nudge">Give it a name you’ll recognise later. Picking a template names it for you.</p>}
          <p className="text-[12px] text-gray-500 mt-1">One email to a chosen set of schools, personalised per school, sent from a real mailbox. Replies land in Needs reply.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-[11px] ${saveState === 'error' ? 'text-red-300' : 'text-gray-600'}`} data-testid="save-state">{saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Draft saved' : saveState === 'error' ? 'Not saved' : ''}</span>
          {saveState === 'error' && <button type="button" onClick={() => settle()} className={`${ghostBtn} border border-white/[0.1] text-gray-100`} data-testid="retry-save">Retry save</button>}
          <button type="button" onClick={() => setPendingDelete(true)} className={ghostBtn} aria-label="Delete draft"><Trash2 className="w-3.5 h-3.5" /> Delete draft</button>
        </div>
      </div>

      <Stepper current={step} ready={stepReady} onJump={(i) => { if (i < step || stepReady.slice(0, i).every(Boolean)) setStep(i); }} />

      {step === 0 && (
        <AudienceStep aud={aud} onChange={(next) => save({ audience: { ...aud, ...next } })} summary={shownSummary} />
      )}
      {step === 1 && (
        <MessageStep
          b={b} save={save} groups={groups} fromOption={fromOption} sendersLoading={!senders}
          onSendTest={sendTest} previewSchool={shownSummary?.recipients?.find((r) => r.status === 'ready') || null} readyIds={readyIds}
          attachments={attachments} onUpload={(files) => uploadFiles(files, null)} onRemove={removeAttachment}
        />
      )}
      {step === 2 && (
        <PreviewStep rendered={rendered} aud={aud} selectedId={selectedId} onSelect={setSelectedId} b={b} attachments={attachments} onUpload={uploadFiles} onRemove={removeAttachment} />
      )}
      {step === 3 && (
        <SendStep b={b} rendered={rendered} fromOption={fromOption} user={user} sending={sending || preparing} error={sendError} onSend={startSend} blocked={!saved} attachments={attachments}
          preparingLong={preparing && preparingLong} onTakeOver={() => { setPreparing(false); setPreparingLong(false); startSend(); }} />
      )}

      <div className="mt-6 pt-4 border-t border-white/[0.06] flex items-center justify-between gap-3">
        <button type="button" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className={ghostBtn}><ChevronLeft className="w-3.5 h-3.5" /> Back</button>
        <span className="text-[11px] text-gray-500">Step {step + 1} of {STEP_LABELS.length}</span>
        {step < 3 ? (
          <button type="button" onClick={async () => { if (await settle()) setStep((s) => s + 1); }} disabled={!stepReady[step]} className={primaryBtn} data-testid="step-continue">
            {step === 2 ? `Continue · ${rendered?.ready ?? ready ?? 0} school${(rendered?.ready ?? ready) === 1 ? '' : 's'}` : 'Continue'} <ChevronRight className="w-3.5 h-3.5" />
          </button>
        ) : <span />}
      </div>

      <ConfirmDialog open={pendingDelete} title="Delete this draft?" description="Nothing has been sent. The draft and its message are removed." confirmLabel="Delete draft" destructive onConfirm={removeDraft} onClose={() => setPendingDelete(false)} />
      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}

// ---------------------------------------------------------------------------

// The audience summary as it stands after removals the server has not
// recounted yet: removed schools drop out of the list and the counts follow.
// Only removals are applied here — anything that could add schools waits for
// the server. A hand-pick dropped from a list that still has others is gone
// too; an emptied hand-pick list falls back to the filters, so that one waits.
function withRemovals(summary, aud) {
  if (!summary || !Array.isArray(summary.recipients) || !aud) return summary;
  const excluded = new Set(aud.exclude_account_ids || []);
  const picked = new Set(aud.account_ids || []);
  const dropped = new Set((summary.audience?.account_ids || []).filter((x) => !picked.has(x)));
  const gone = (r) => excluded.has(r.accountId) || (picked.size > 0 && dropped.has(r.accountId));
  const removed = summary.recipients.filter(gone).length;
  if (!removed) return summary;
  const recipients = summary.recipients.filter((r) => !gone(r));
  const ready = recipients.filter((r) => r.status === 'ready').length;
  const reasons = {};
  for (const r of recipients) if (r.status === 'skipped' && r.skipReason) reasons[r.skipReason] = (reasons[r.skipReason] || 0) + 1;
  return { ...summary, recipients, ready, skipped: recipients.length - ready, reasons, total: Math.max(recipients.length, (summary.total ?? recipients.length) - removed) };
}

function Stepper({ current, ready, onJump }) {
  return (
    <div className="flex items-center gap-1.5 mb-6 flex-wrap" role="list" aria-label="Broadcast steps">
      {STEP_LABELS.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <React.Fragment key={label}>
            <button type="button" role="listitem" aria-current={active ? 'step' : undefined} onClick={() => onJump(i)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[12.5px] ${active ? 'border-[#B0FC38]/50 bg-[#B0FC38]/[0.08] text-white' : done ? 'border-white/[0.08] text-gray-200' : 'border-white/[0.08] text-gray-500'}`}>
              <span className={`w-5 h-5 rounded-full inline-grid place-items-center text-[10px] font-bold ${active || done ? 'bg-[#B0FC38] text-gray-900' : 'bg-white/[0.06] text-gray-400'}`}>{done && ready[i] ? <Check className="w-3 h-3" /> : i + 1}</span>
              {label}
            </button>
            {i < STEP_LABELS.length - 1 && <span className="w-4 h-px bg-white/[0.08]" aria-hidden="true" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function Field({ label, hint, children, required }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">{label}{required && <span className="text-[#B0FC38]"> *</span>}</div>
      {children}
      {hint && <p className="mt-1 text-[10.5px] text-gray-500">{hint}</p>}
    </div>
  );
}

// ---- Step 1: Audience ---------------------------------------------------------------

function AudienceStep({ aud, onChange, summary }) {
  const [showList, setShowList] = useState(false);
  const [showSkipped, setShowSkipped] = useState(false);
  const stages = aud.stages || [];
  const roles = aud.roles || ['principal'];
  const titles = aud.titles || [];
  const picked = aud.account_ids || [];
  const excluded = aud.exclude_account_ids || [];
  // Custom job titles in use on school contacts (contacts.role_title).
  const [titleOptions, setTitleOptions] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get('/api/admin/communications/contact-titles').then((d) => { if (alive) setTitleOptions(d?.titles || []); }).catch(() => { if (alive) setTitleOptions([]); });
    return () => { alive = false; };
  }, []);
  const nameFor = (accountId) => summary?.recipients?.find((r) => r.accountId === accountId)?.accountName || accountId;
  const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const skipped = (summary?.recipients || []).filter((r) => r.status === 'skipped');
  const readyList = (summary?.recipients || []).filter((r) => r.status === 'ready');

  // ---- removing single schools ----
  // Names of schools removed on this visit, until the server's recount names
  // them (summary.excluded).
  const [removedNames, setRemovedNames] = useState({});
  const excludedName = (accountId) => {
    if (removedNames[accountId]) return removedNames[accountId];
    const known = summary?.excluded?.find((x) => x.accountId === accountId);
    if (known) return known.accountName || 'A school no longer in the CRM';
    return '…';
  };
  const hasFilters = stages.length > 0 || !!aud.borough || !!aud.district || !!aud.type;
  // A removed row takes its focus with it: keyboard users land on the next
  // row's Remove (or the nearest control that is still there).
  const asideRef = useRef(null);
  const focusNextRef = useRef(null);
  useEffect(() => {
    const want = focusNextRef.current;
    if (!want) return;
    focusNextRef.current = null;
    const root = asideRef.current;
    if (!root) return;
    const els = Array.from(root.querySelectorAll(want.selector));
    const el = els.length ? els[Math.min(want.index, els.length - 1)] : want.fallbacks.map((sel) => root.querySelector(sel)).find(Boolean) || root;
    el.focus();
  });
  const removeSchool = (r, list, index) => {
    setRemovedNames((m) => ({ ...m, [r.accountId]: r.accountName }));
    focusNextRef.current = { selector: `[data-remove-list="${list}"]`, index, fallbacks: ['[data-restore]', '[data-testid="show-list"]'] };
    if (picked.includes(r.accountId)) {
      // A hand-pick is simply un-picked. When that empties the hand-picks the
      // filters alone decide again — and may match this school — so it is
      // also removed from them.
      const rest = picked.filter((x) => x !== r.accountId);
      onChange(rest.length || !hasFilters ? { account_ids: rest } : { account_ids: rest, exclude_account_ids: [...excluded, r.accountId] });
    } else if (!excluded.includes(r.accountId)) {
      onChange({ exclude_account_ids: [...excluded, r.accountId] });
    }
  };
  const restoreSchool = (accountId, index) => {
    focusNextRef.current = { selector: '[data-restore]', index, fallbacks: ['[data-testid="show-list"]'] };
    onChange({ exclude_account_ids: excluded.filter((x) => x !== accountId) });
  };
  const restoreAll = () => {
    focusNextRef.current = { selector: '[data-testid="show-list"]', index: 0, fallbacks: [] };
    onChange({ exclude_account_ids: [] });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-5" data-testid="audience-step">
      <div className="space-y-5">
        <Field label="Schools at these stages" hint="Leave empty to match any stage — but pick at least one filter of some kind.">
          <div className="flex flex-wrap gap-1.5">
            {STAGES.map(([v, label]) => <button key={v} type="button" onClick={() => onChange({ stages: toggle(stages, v) })} className={chip(stages.includes(v))} aria-pressed={stages.includes(v)}>{label}</button>)}
          </div>
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Borough">
            <select value={aud.borough || ''} onChange={(e) => onChange({ borough: e.target.value })} className={inputCls} aria-label="Borough">
              <option value="">Any</option>
              {BOROUGHS.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
            </select>
          </Field>
          <Field label="District">
            <input value={aud.district || ''} onChange={(e) => onChange({ district: e.target.value.replace(/[^\d]/g, '') })} placeholder="Any" className={inputCls} aria-label="District" inputMode="numeric" />
          </Field>
          <Field label="Account type">
            <select value={aud.type || ''} onChange={(e) => onChange({ type: e.target.value })} className={inputCls} aria-label="Account type">
              <option value="">Any</option>
              <option value="school">Schools</option>
              <option value="district">District offices</option>
              <option value="external">External</option>
            </select>
          </Field>
        </div>
        <Field label="Or hand-pick schools" hint="Added on top of the filters above. Handy for a launch note to two or three schools.">
          <div className="flex flex-wrap gap-1.5 items-center">
            {picked.map((accountId) => (
              <span key={accountId} className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full border border-white/[0.1] bg-white/[0.04] text-[12px] text-gray-100" data-testid="picked-school">
                {nameFor(accountId)}
                <button type="button" onClick={() => onChange({ account_ids: picked.filter((x) => x !== accountId) })} aria-label={`Remove ${nameFor(accountId)}`} className="p-0.5 rounded-full text-gray-500 hover:text-white"><X className="w-3 h-3" /></button>
              </span>
            ))}
            {/* Picking a school that was removed puts it back. */}
            <div className="min-w-[240px]"><AccountPicker value="" emptyLabel="Add a school…" onChange={(accountId) => { if (accountId && !picked.includes(accountId)) onChange({ account_ids: [...picked, accountId], ...(excluded.includes(accountId) ? { exclude_account_ids: excluded.filter((x) => x !== accountId) } : {}) }); }} /></div>
          </div>
        </Field>
        <Field label="Who at each school" required hint="The email goes to the first matching contact who has a usable address — or to everyone matching, if you tick the box.">
          <div className="flex flex-wrap gap-1.5">
            {CONTACT_ROLE_OPTIONS.map((r) => <button key={r.value} type="button" onClick={() => { const next = toggle(roles, r.value); if (next.length || titles.length) onChange({ roles: next }); }} className={chip(roles.includes(r.value))} aria-pressed={roles.includes(r.value)}>{r.label}</button>)}
          </div>
          {titleOptions && titleOptions.length > 0 && (
            <div className="mt-3" data-testid="audience-titles">
              <div className="text-[11px] text-gray-500 mb-1.5">Custom job titles on file <span className="text-gray-600">— the exact title typed on a contact, matched whatever the role</span></div>
              <div className="flex flex-wrap gap-1.5">
                {titleOptions.map((t) => (
                  <button key={t.key} type="button" onClick={() => { const next = toggle(titles, t.key); if (next.length || roles.length) onChange({ titles: next }); }} className={chip(titles.includes(t.key))} aria-pressed={titles.includes(t.key)} title={`${t.count} contact${t.count === 1 ? '' : 's'}, ${t.withEmail} with email`} data-testid={`title-chip-${t.key}`}>
                    {t.label} <span className="opacity-60">· {t.count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <label className="mt-3 flex items-start gap-2.5 text-[12.5px] text-gray-300 cursor-pointer">
            <input type="checkbox" checked={!!aud.combine} onChange={(e) => onChange({ combine: e.target.checked })} className="accent-[#B0FC38] mt-0.5" />
            <span>Put everyone matching at a school in the same email’s To line <span className="text-gray-500">— they share one reply thread.</span></span>
          </label>
        </Field>
      </div>

      <aside ref={asideRef} tabIndex={-1} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 self-start lg:sticky lg:top-4 focus:outline-none" data-testid="audience-summary">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Who receives this</div>
        {!audienceHasCriteria(aud) ? (
          <p className="text-[12.5px] text-gray-400">Pick a stage, a borough, a district, a type, or hand-pick schools. A broadcast never goes to every school by default.</p>
        ) : !summary ? (
          <div className="flex items-center gap-2 text-[12.5px] text-gray-400"><Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Counting…</div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-[15px] text-white font-semibold tabular-nums"><Users2 className="w-4 h-4 text-[#B0FC38]" aria-hidden="true" /><span data-testid="audience-count">{summary.ready}</span> <span className="text-[13px] font-normal text-gray-400">school{summary.ready === 1 ? '' : 's'} will receive it</span></div>
            <div className="text-[12px] text-gray-500 mt-1">{audienceSummary(aud)}</div>
            {summary.truncated && <div className="mt-2 text-[11.5px] text-amber-300">More than {summary.recipients.length} schools match — only the first {summary.recipients.length} (by name) are included. Narrow the audience.</div>}
          </>
        )}
        {excluded.length > 0 && (
          <div className="mt-3 rounded-lg border border-white/[0.08] bg-white/[0.02] p-2.5" data-testid="removed-schools">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] uppercase tracking-wider text-gray-400" id="removed-schools-label">Removed schools ({excluded.length})</span>
              <button type="button" onClick={restoreAll} className="min-h-[24px] px-1.5 rounded text-[11.5px] text-[#B0FC38] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B0FC38]/70" data-testid="restore-all">Restore all</button>
            </div>
            <ul className="mt-1.5 flex flex-wrap gap-1.5 list-none m-0 p-0" aria-labelledby="removed-schools-label">
              {excluded.map((accountId, i) => (
                <li key={accountId} className="inline-flex items-center gap-1 pl-2.5 pr-0.5 py-0.5 rounded-full border border-white/[0.1] bg-white/[0.04] text-[12px] text-gray-200 max-w-full" data-testid="removed-school">
                  <span className="truncate">{excludedName(accountId)}</span>
                  <button type="button" data-restore onClick={() => restoreSchool(accountId, i)} aria-label={`Restore ${excludedName(accountId)} to this broadcast`} title="Put this school back"
                    className="shrink-0 inline-grid place-items-center w-6 h-6 rounded-full text-gray-400 hover:text-white hover:bg-white/[0.08] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B0FC38]/70">
                    <Undo2 className="w-3.5 h-3.5" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {audienceHasCriteria(aud) && summary && (
          <>
            {summary.skipped > 0 && (
              <div className="mt-3 rounded-lg border border-amber-500/25 bg-amber-500/[0.06] p-2.5 text-[12px] text-amber-100" data-testid="audience-skipped">
                <div className="flex items-start gap-2"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-amber-300 shrink-0" aria-hidden="true" /><div><b className="text-amber-300">{summary.skipped} of {summary.total} will be skipped.</b> {Object.entries(summary.reasons || {}).map(([k, n]) => `${n} ${skipReasonLabel(k, roles, aud.titles).toLowerCase()}`).join(' · ')}</div></div>
                <button type="button" onClick={() => setShowSkipped((v) => !v)} aria-expanded={showSkipped} className="mt-1.5 text-[11.5px] text-amber-300 hover:text-white">{showSkipped ? 'Hide' : 'Which schools?'}</button>
                {showSkipped && (
                  <ul className="mt-1.5 space-y-1 list-none m-0 p-0">
                    {skipped.map((r, i) => (
                      <li key={r.accountId} className="flex items-center justify-between gap-2" data-testid="skipped-school">
                        <span className="min-w-0">
                          <Link to={`/admin/accounts/${r.accountId}?tab=contacts`} className="block text-gray-200 hover:text-white truncate">{r.accountName}</Link>
                          <span className="block text-[11px] text-amber-200/80 truncate">{skipReasonLabel(r.skipReason, roles, aud.titles)}</span>
                        </span>
                        <RemoveSchoolButton name={r.accountName} list="skipped" onClick={() => removeSchool(r, 'skipped', i)} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {readyList.length > 0 && (
              <div className="mt-3">
                <button type="button" onClick={() => setShowList((v) => !v)} aria-expanded={showList} className="text-[11.5px] text-[#B0FC38] hover:text-white" data-testid="show-list">{showList ? 'Hide list' : 'Show the list · remove schools'}</button>
                {showList && (
                  <>
                    <p className="mt-1 text-[11px] text-gray-400">Remove a school to leave it out of this broadcast. Removed schools stay listed above until you put them back.</p>
                    <ul className="mt-1.5 max-h-72 overflow-y-auto space-y-1 list-none m-0 p-0 text-[12px]" data-testid="audience-list">
                      {readyList.map((r, i) => (
                        <li key={r.accountId} className="flex items-center justify-between gap-2" data-testid="audience-school">
                          <span className="min-w-0">
                            <span className="block text-gray-200 truncate">{r.accountName}</span>
                            <span className="block text-[11px] text-gray-400 truncate">{r.to.map((t) => shortName(t.name, t.email)).join(', ')}</span>
                          </span>
                          <RemoveSchoolButton name={r.accountName} list="ready" onClick={() => removeSchool(r, 'ready', i)} />
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </aside>
    </div>
  );
}

function RemoveSchoolButton({ name, list, onClick }) {
  return (
    <button type="button" onClick={onClick} data-remove-list={list} aria-label={`Remove ${name} from this broadcast`}
      className="shrink-0 min-h-[24px] px-2 py-0.5 rounded-md border border-white/[0.12] text-[11px] text-gray-200 hover:text-white hover:border-red-400/50 hover:bg-red-500/[0.1] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B0FC38]/70">
      Remove
    </button>
  );
}

// ---- Step 2: Message ----------------------------------------------------------------

function MessageStep({ b, save, groups, fromOption, sendersLoading, onSendTest, previewSchool, readyIds, attachments = [], onUpload, onRemove }) {
  const forAll = attachments.filter((a) => !a.accountId);
  const [pickerOpen, setPickerOpen] = useState(false);
  // A template also names an unnamed draft: "Untitled broadcast" was every
  // broadcast's name because nothing ever set one.
  const insertTemplate = (t) => save({ templateId: t.id, subjectTemplate: t.subjectTemplate, bodyTemplateHtml: t.bodyTemplateHtml, bodyTemplateText: t.bodyTemplateText, purpose: t.purpose || b.purpose, ...(isUntitled(b.name) && t.name ? { name: t.name } : {}) });
  return (
    <div className="space-y-5" data-testid="message-step">
      <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] divide-y divide-white/[0.06]">
        <div className="grid grid-cols-[72px_minmax(0,1fr)] gap-3 items-start px-3 py-2.5">
          <span className="text-[11px] uppercase tracking-wider text-gray-500 pt-1.5">From</span>
          <div className="flex flex-col gap-1 min-w-0">
            <FromPicker groups={groups} value={b.fromEmail || ''} selectedMailboxId={fromOption?.mailboxId || null} option={fromOption} loading={sendersLoading} error="" open={pickerOpen} onOpenChange={setPickerOpen}
              onChange={(email, o) => { save({ fromEmail: email, fromName: o?.name || null, mailboxId: o?.mailboxId || null }); setPickerOpen(false); }} />
            {fromOption && <span className="text-[11.5px] text-gray-500">{fromOption.isMine ? 'Your mailbox' : `${firstName(fromOption.ownerName)}’s mailbox · you’re allowed to send as ${firstName(fromOption.ownerName)}`} · each school’s email lands in this Sent folder; replies come back here and to Needs reply.{fromOption.signature ? '' : ' No Gmail signature is set for this address.'}</span>}
          </div>
        </div>
        <CopiesRow label="Cc" field="cc" value={b.cc || []} onChange={(v) => save({ cc: v })} hint="Visible to the school. Goes on every school’s email." />
        <CopiesRow label="Bcc" field="bcc" value={b.bcc || []} onChange={(v) => save({ bcc: v })} hint={`Hidden from the school. Goes on every school’s email, so ${b.audience?.account_ids?.length || 'many'} schools means that many copies in each Bcc inbox.`} />
        <div className="grid grid-cols-[72px_minmax(0,1fr)] gap-3 items-start px-3 py-2.5">
          <span className="text-[11px] uppercase tracking-wider text-gray-500 pt-1.5">Purpose</span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label="Purpose">
            {Object.entries(PURPOSE_COPY).map(([k, v]) => (
              <label key={k} className={`rounded-lg border p-2.5 cursor-pointer ${b.purpose === k ? 'border-[#B0FC38]/40 bg-[#B0FC38]/[0.06]' : 'border-white/[0.06] hover:border-white/[0.12]'}`}>
                <div className="flex items-start gap-2">
                  <input type="radio" name="purpose" value={k} checked={b.purpose === k} onChange={() => save({ purpose: k })} className="accent-[#B0FC38] mt-0.5" />
                  <div><div className="text-[12.5px] text-white font-medium">{v.label}</div><div className="text-[10.5px] text-gray-400 mt-0.5 leading-relaxed">{v.detail}</div></div>
                </div>
              </label>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-[72px_minmax(0,1fr)] gap-3 items-center px-3 py-2.5">
          <span className="text-[11px] uppercase tracking-wider text-gray-500">Template</span>
          <div className="flex items-center gap-3 flex-wrap"><TemplatePicker onPick={insertTemplate} label="Start from a template" /><span className="text-[11.5px] text-gray-500">Tokens fill in from each school’s record.</span></div>
        </div>
        <div className="grid grid-cols-[72px_minmax(0,1fr)] gap-3 items-start px-3 py-2.5" data-testid="attachments-all">
          <span className="text-[11px] uppercase tracking-wider text-gray-500 pt-1.5">Files</span>
          <div className="min-w-0">
            <AttachmentChips items={forAll} onRemove={onRemove} emptyText="No files yet." />
            <div className="mt-1.5 flex items-center gap-3 flex-wrap">
              <FilePick label="Attach for every school" onFiles={onUpload} testId="attach-all" />
              <span className="text-[11.5px] text-gray-500">Every school gets these. Files for one school only are added on the Preview step. Up to 10 MB each, 20 MB per school.</span>
            </div>
          </div>
        </div>
      </div>

      <TemplateEditor
        subject={b.subjectTemplate} onSubjectChange={(v) => save({ subjectTemplate: v })}
        bodyHtml={b.bodyTemplateHtml} onBodyHtmlChange={(v) => save({ bodyTemplateHtml: v })}
        bodyText={b.bodyTemplateText} onBodyTextChange={(v) => save({ bodyTemplateText: v })}
        sender={{ name: b.fromName || fromOption?.name || '', email: b.fromEmail || '' }}
        onSendTest={onSendTest}
        testHint={(previewed) => {
          // The preview can show any school; the test is always one this broadcast will email.
          if (previewed?.id && readyIds?.has(previewed.id)) return `Sends you ${previewed.name}’s version`;
          if (!previewSchool) return null;
          return previewed?.id
            ? `${previewed.name} is not in this broadcast. The test sends ${previewSchool.accountName}’s version`
            : `Sends you ${previewSchool.accountName}’s version`;
        }}
      />
    </div>
  );
}

// Cc / Bcc: a comma-separated list, saved as an array when the field loses focus
// or Enter is pressed. Typing is local so the autosave does not fire per keystroke.
function CopiesRow({ label, field, value, onChange, hint }) {
  const [text, setText] = useState(value.join(', '));
  const joined = value.join(', ');
  useEffect(() => { setText(joined); }, [joined]);
  const commit = () => {
    const list = [...new Set(text.split(/[,;\s]+/).map((s) => s.trim().toLowerCase()).filter(Boolean))];
    if (list.join(', ') !== joined) onChange(list);
    setText(list.join(', '));
  };
  const bad = text.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean).filter((s) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s));
  return (
    <div className="grid grid-cols-[72px_minmax(0,1fr)] gap-3 items-start px-3 py-2.5">
      <label htmlFor={`broadcast-${field}`} className="text-[11px] uppercase tracking-wider text-gray-500 pt-2">{label}</label>
      <div className="min-w-0">
        <input id={`broadcast-${field}`} type="text" value={text} onChange={(e) => setText(e.target.value)} onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
          placeholder="name@example.org, other@example.org" className={inputCls} data-testid={`broadcast-${field}`} aria-invalid={bad.length ? 'true' : undefined} />
        <span className="text-[11.5px] text-gray-500">{bad.length ? <span className="text-amber-300">Not an address: {bad.join(', ')}. </span> : null}{hint}</span>
      </div>
    </div>
  );
}

// ---- Step 3: Preview each school ------------------------------------------------------

function PreviewStep({ rendered, aud, selectedId, onSelect, b, attachments = [], onUpload, onRemove }) {
  const forAll = attachments.filter((a) => !a.accountId);
  const ownFor = (accountId) => attachments.filter((a) => a.accountId === accountId);
  const roles = aud.roles || ['principal'];
  if (!rendered) return <div className="flex items-center gap-2 text-[13px] text-gray-400 py-10 justify-center"><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Rendering every school’s email…</div>;
  if (rendered.error) return <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">{rendered.error}</div>;
  const list = rendered.recipients || [];
  const sel = list.find((r) => r.accountId === selectedId) || null;
  const failing = Object.entries(rendered.checks || {}).filter(([, ok]) => !ok).map(([k]) => k);
  const checkCopy = { criteria: 'choose an audience', subject: 'add a subject', body: 'write the message', sender: rendered.sender?.reason === 'revoked' ? `you’re not allowed to send from ${rendered.sender?.email}` : rendered.sender?.reason === 'no_from' ? 'choose a From address' : `${rendered.sender?.email || 'the From address'} is not a connected mailbox`, unsubscribe: 'unsubscribe links are not configured for commercial email' };

  return (
    <div data-testid="preview-step">
      {rendered.skipped > 0 && (
        <div className="mb-3 rounded-lg border border-amber-500/25 bg-amber-500/[0.06] p-3 text-[12.5px] text-amber-100 flex items-start gap-2" data-testid="skipped-banner">
          <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" aria-hidden="true" />
          <div><b className="text-amber-300">{rendered.skipped} of {rendered.total} school{rendered.total === 1 ? '' : 's'} will be skipped.</b> {list.filter((r) => r.status === 'skipped').slice(0, 4).map((r) => `${r.accountName} — ${skipReasonLabel(r.skipReason, roles, aud.titles).toLowerCase()}`).join(' · ')}{rendered.skipped > 4 ? ` · and ${rendered.skipped - 4} more` : ''}</div>
        </div>
      )}
      {failing.length > 0 && (
        <div className="mb-3 rounded-lg border border-red-500/25 bg-red-500/[0.06] p-3 text-[12.5px] text-red-200" role="alert" data-testid="preview-checks">
          Before this can send: {failing.map((k) => checkCopy[k] || k).join(', ')}.
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] gap-4 items-start">
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
          <div className="px-3.5 py-2.5 border-b border-white/[0.06] flex items-center justify-between gap-2 text-[11px] uppercase tracking-wider text-gray-500">
            <span>Recipients · {rendered.ready} sending{rendered.skipped ? `, ${rendered.skipped} skipped` : ''}</span>
          </div>
          <ul className="max-h-[560px] overflow-y-auto list-none m-0 p-0" data-testid="preview-list">
            {list.map((r) => {
              const on = r.accountId === selectedId;
              const skipped = r.status === 'skipped';
              return (
                <li key={r.accountId}>
                  <button type="button" onClick={() => !skipped && onSelect(r.accountId)} disabled={skipped} aria-pressed={on}
                    className={`w-full text-left grid grid-cols-[minmax(0,1fr)_auto] gap-2 px-3.5 py-2.5 border-b border-white/[0.05] ${on ? 'bg-[#B0FC38]/[0.07]' : 'hover:bg-white/[0.03]'} ${skipped ? 'opacity-60 cursor-default' : ''}`}>
                    <span className="min-w-0">
                      <span className="block text-[13px] text-white font-medium truncate">{r.accountName}</span>
                      <span className={`block text-[11.5px] truncate ${skipped ? (r.skipReason === 'suppressed' ? 'text-red-300' : 'text-amber-300') : 'text-gray-500'}`}>
                        {skipped ? `${skipReasonLabel(r.skipReason, roles, aud.titles)} · skipped` : r.to.map((t) => `${shortName(t.name, t.email)}${r.contactRole ? ` · ${contactRoleLabel(r.contactRole)}` : ''}`).join(', ')}
                      </span>
                    </span>
                    <span className="text-[11px] text-gray-600 tabular-nums inline-flex items-center gap-1">{ownFor(r.accountId).length > 0 && <Paperclip className="w-3 h-3 text-[#B0FC38]" aria-label="Has its own attachment" />}{r.dbn || ''}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-4" data-testid="preview-pane">
          {!sel ? <div className="text-[12.5px] text-gray-500 py-10 text-center">No school to preview.</div> : (
            <>
              <div className="text-[11.5px] text-gray-500 space-y-0.5 pb-2.5 border-b border-white/[0.06]">
                <div className="truncate">From: <b className="text-gray-200 font-medium">{rendered.sender?.name ? `${rendered.sender.name} <${rendered.sender.email}>` : rendered.sender?.email}</b></div>
                <div className="truncate">To: <b className="text-gray-200 font-medium">{sel.to.map((t) => (t.name ? `${t.name} <${t.email}>` : t.email)).join(', ')}</b></div>
                <div className="truncate">Subject: <b className="text-gray-200 font-medium" data-testid="preview-subject">{sel.subject}</b></div>
              </div>
              <iframe title={`Preview for ${sel.accountName}`} sandbox="" srcDoc={wrapHtml(sel.html || '')} className="mt-3 w-full h-[440px] rounded-md border border-white/[0.08] bg-white" data-testid="preview-frame" />
              <div className="mt-3 pt-2.5 border-t border-white/[0.06]" data-testid="preview-attachments">
                <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">Attachments for {sel.accountName}</div>
                <AttachmentChips items={[...forAll.map((a) => ({ ...a, scope: 'all' })), ...ownFor(sel.accountId).map((a) => ({ ...a, scope: 'school' }))]} onRemove={(a) => (a.scope === 'school' ? onRemove?.(a) : null)} emptyText="None." />
                {b.status === 'draft' && <div className="mt-1.5"><FilePick label={`Attach a file for ${shortName(sel.accountName, '')} only`} onFiles={(files) => onUpload?.(files, sel.accountId)} testId="attach-school" /></div>}
              </div>
              <div className="mt-2.5 pt-2.5 border-t border-white/[0.06] text-[11px] text-gray-600">
                {b.purpose === 'commercial' ? 'Commercial message — address and one-click unsubscribe footer added automatically.' : 'Service correspondence — no marketing footer.'}{rendered.sender?.hasSignature ? ` Gmail signature for ${rendered.sender.email} included.` : ''}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Step 4: Send -----------------------------------------------------------------

function SendStep({ b, rendered, fromOption, user, sending, error, onSend, blocked = false, preparingLong = false, onTakeOver = null, attachments = [] }) {
  const n = rendered?.ready ?? 0;
  const forAll = attachments.filter((a) => !a.accountId);
  const perSchool = attachments.filter((a) => a.accountId);
  const attachmentsLine = !attachments.length ? 'None' : [forAll.length ? `${forAll.length} for every school (${formatBytes(forAll.reduce((s, a) => s + (a.size || 0), 0))})` : null, perSchool.length ? `${perSchool.length} school-specific across ${new Set(perSchool.map((a) => a.accountId)).size} school${new Set(perSchool.map((a) => a.accountId)).size === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ');
  const from = b.fromEmail ? `${b.fromName || fromOption?.name || ''} <${b.fromEmail}>`.trim() : '—';
  return (
    <div className="max-w-2xl mx-auto rounded-xl border border-white/[0.08] bg-white/[0.02] p-5" data-testid="send-step">
      <h2 className="text-[16px] text-white font-semibold">Send to {n} school{n === 1 ? '' : 's'}</h2>
      <dl className="mt-3 text-[12.5px] divide-y divide-white/[0.05]">
        <Row k="Audience" v={audienceSummary(b.audience)} />
        <Row k="From" v={from} />
        {(b.cc?.length || b.bcc?.length) ? <Row k="Copies" v={[b.cc?.length ? `Cc ${b.cc.join(', ')}` : null, b.bcc?.length ? `Bcc ${b.bcc.join(', ')}` : null].filter(Boolean).join(' · ')} /> : null}
        <Row k="Recorded as" v={`sent from ${localPart(b.fromEmail) || 'the chosen mailbox'} by ${firstName(user?.name) || 'you'}`} />
        <Row k="Purpose" v={PURPOSE_COPY[b.purpose]?.label || b.purpose} />
        <Row k="Attachments" v={attachmentsLine} />
        <Row k="Skipped" v={rendered?.skipped ? `${rendered.skipped} school${rendered.skipped === 1 ? '' : 's'} (named on the previous step and in the report)` : 'None'} />
      </dl>
      <p className="mt-4 text-[12px] text-gray-500">Each school gets its own email through {localPart(fromOption?.email || b.fromEmail) || 'the mailbox'}’s Gmail, a few seconds apart. You can leave this page — sending continues, and the report fills in here.</p>
      {error && (
        <div role="alert" className="mt-3 rounded-lg border border-red-500/25 bg-red-500/[0.08] px-3 py-2.5 text-[12px] text-red-200" data-testid="send-error">{error.title}</div>
      )}
      {sending && !error && (
        <div role="status" className="mt-3 rounded-lg border border-amber-500/25 bg-amber-500/[0.06] px-3 py-2.5 text-[12px] text-amber-100 flex items-center justify-between gap-3" data-testid="send-preparing">
          <span>{preparingLong ? 'Preparing is taking longer than expected. The request that started it may have been interrupted.' : 'Preparing the recipient list…'}</span>
          {preparingLong && onTakeOver && <button type="button" onClick={onTakeOver} className={`${ghostBtn} border border-white/[0.1] text-gray-100`} data-testid="send-takeover">Try again</button>}
        </div>
      )}
      <div className="mt-5 flex items-center justify-end gap-2">
        {blocked && <span className="text-[12px] text-red-300 mr-auto" role="alert">Your latest changes aren’t saved. Use “Retry save” before sending.</span>}
        <button type="button" onClick={onSend} disabled={sending || blocked || n === 0 || !rendered?.canSend} className={primaryBtn} data-testid="send-button">
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> : <Send className="w-3.5 h-3.5" aria-hidden="true" />}
          {sending ? 'Sending…' : `Send to ${n} school${n === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  );
}

function AttachmentChips({ items, onRemove, emptyText }) {
  if (!items.length) return <div className="text-[12px] text-gray-500">{emptyText}</div>;
  return (
    <ul className="flex flex-wrap gap-1.5 list-none m-0 p-0">
      {items.map((a) => (
        <li key={a.id} className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full border border-white/[0.1] bg-white/[0.04] text-[12px] text-gray-100" data-testid="attachment-chip" title={`${a.filename} · ${formatBytes(a.size)}`}>
          <Paperclip className="w-3 h-3 text-gray-500" aria-hidden="true" />
          <span className="truncate max-w-[220px]">{a.filename}</span>
          <small className="text-gray-500">{formatBytes(a.size)}{a.scope === 'all' ? ' · everyone' : ''}</small>
          {onRemove && a.scope !== 'all' && (
            <button type="button" onClick={() => onRemove(a)} aria-label={`Remove ${a.filename}`} className="p-0.5 rounded-full text-gray-500 hover:text-white hover:bg-white/[0.08]"><X className="w-3 h-3" aria-hidden="true" /></button>
          )}
        </li>
      ))}
    </ul>
  );
}

function FilePick({ label, onFiles, testId }) {
  return (
    <label className={`${ghostBtn} border border-white/[0.1] text-gray-200 cursor-pointer`}>
      <Paperclip className="w-3.5 h-3.5" aria-hidden="true" /> {label}
      <input type="file" multiple className="sr-only" data-testid={testId} onChange={(e) => { const files = Array.from(e.target.files || []); e.target.value = ''; if (files.length) onFiles?.(files); }} />
    </label>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between gap-4 py-1.5"><dt className="text-gray-500 shrink-0">{k}</dt><dd className="text-gray-200 text-right m-0">{v}</dd></div>;
}

// ---- Report (sending / sent / cancelled) ---------------------------------------------

// "Untitled broadcast" and its "(copy)" variants: the placeholder, not a name.
export function isUntitled(name) { return /^untitled broadcast( \(copy\))*$/i.test(String(name || '').trim()); }

// Inline rename on a sent broadcast: the name is a label for the list and the
// report and can change at any time; nothing else can.
function ReportName({ b, onRename }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(b.name);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!editing) setValue(b.name); }, [b.name, editing]);
  const commit = async () => {
    const next = value.trim();
    if (!next || next === b.name) { setEditing(false); setValue(b.name); return; }
    setBusy(true);
    try { await onRename(next); setEditing(false); } finally { setBusy(false); }
  };
  if (!editing) {
    return (
      <button type="button" onClick={() => setEditing(true)} className="group inline-flex items-center gap-2 text-left rounded-md -mx-1 px-1 hover:bg-white/[0.04]" title="Rename" data-testid="report-name">
        <h1 className={`text-xl md:text-2xl font-semibold tracking-tight ${isUntitled(b.name) ? 'text-gray-400' : 'text-white'}`}>{b.name}</h1>
        <Pencil className="w-3.5 h-3.5 text-gray-600 group-hover:text-gray-300" aria-hidden="true" />
        <span className="sr-only">Rename broadcast</span>
      </button>
    );
  }
  return (
    <form onSubmit={(e) => { e.preventDefault(); commit(); }} className="flex items-center gap-2">
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Escape') { setEditing(false); setValue(b.name); } }}
        aria-label="Broadcast name"
        data-testid="report-name-input"
        disabled={busy}
        className="rounded-lg px-3 py-1.5 text-lg md:text-xl font-semibold tracking-tight text-white bg-white/[0.03] border border-[#B0FC38]/50 focus:outline-none min-w-[16rem]"
      />
    </form>
  );
}

function Report({ b, recipients, progress, sendError, onRetry, onCancel, onDuplicate, onDelete = null, onRename }) {
  const meta = broadcastStatusMeta(b.status);
  const p = progress || { total: b.recipientCount, sent: b.sentCount, failed: b.failedCount, skipped: b.skippedCount, pending: 0, sending: 0 };
  const doneCount = p.sent + p.failed + p.skipped;
  const pct = p.total ? Math.round((doneCount / p.total) * 100) : 0;
  const roles = b.audience?.roles || [];
  const groupsOrder = ['failed', 'sending', 'pending', 'sent', 'skipped'];
  return (
    <div>
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap"><ReportName b={b} onRename={onRename} /><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${meta.tone}`}>{meta.label}</span></div>
          <p className="text-[12.5px] text-gray-500 mt-1">{audienceSummary(b.audience)} · from {b.fromEmail}{b.sentByName ? ` · sent by ${firstName(b.sentByName)}` : ''}{b.finishedAt ? ` · finished ${messageTime(b.finishedAt)}` : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          {b.status === 'sending' && <button type="button" onClick={onCancel} className={`${ghostBtn} border border-white/[0.08] text-red-300 hover:text-red-200`}>Stop sending</button>}
          <button type="button" onClick={onDuplicate} className={`${ghostBtn} border border-white/[0.08]`}><Copy className="w-3.5 h-3.5" /> Duplicate</button>
          {onDelete && <button type="button" onClick={onDelete} className={`${ghostBtn} border border-white/[0.08] text-red-300/80 hover:text-red-300`} data-testid="delete-broadcast"><Trash2 className="w-3.5 h-3.5" /> {b.sentCount > 0 ? 'Remove' : 'Delete'}</button>}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px rounded-xl overflow-hidden border border-white/[0.06] bg-white/[0.06] mb-4" data-testid="report-strip">
        <Stat label="Sent" v={p.sent} tone="text-emerald-300" />
        <Stat label="Failed" v={p.failed} tone={p.failed ? 'text-red-300' : 'text-gray-300'} />
        <Stat label="Skipped" v={p.skipped} tone="text-gray-300" />
        <Stat label={b.status === 'sending' ? 'Remaining' : 'Total'} v={b.status === 'sending' ? p.pending + p.sending : p.total} tone="text-white" />
      </div>

      {b.status === 'sending' && (
        <div className="mb-4" data-testid="report-progress">
          <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden"><div className="h-full bg-[#B0FC38] transition-all" style={{ width: `${pct}%` }} /></div>
          <div className="mt-1.5 text-[11.5px] text-gray-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" /> Sending… {doneCount} of {p.total} done.</div>
        </div>
      )}
      {(sendError || b.lastError) && b.status === 'sending' && (
        <div role="alert" className="mb-4 rounded-lg border border-amber-500/25 bg-amber-500/[0.08] px-3 py-2.5 text-[12px] text-amber-100 flex items-center justify-between gap-3">
          <span>{sendError?.title || b.lastError}</span>
          <button type="button" onClick={onRetry} className={`${ghostBtn} border border-white/[0.1] text-gray-100`}>Try again</button>
        </div>
      )}

      <ul className="rounded-xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.05] list-none m-0 p-0" data-testid="report-recipients">
        {groupsOrder.flatMap((status) => recipients.filter((r) => r.status === status)).map((r) => {
          const m = RECIPIENT_STATUS_META[r.status] || RECIPIENT_STATUS_META.pending;
          return (
            <li key={r.id} className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 px-3.5 py-2.5 items-center" data-testid={`recipient-${r.status}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <Link to={`/admin/accounts/${r.accountId}?tab=communications`} className="text-[13px] text-white font-medium truncate hover:text-[#B0FC38]">{r.accountName}</Link>
                  {r.status === 'sent' && r.conversationId && <ExternalLink className="w-3 h-3 text-gray-600" aria-hidden="true" />}
                </div>
                <div className="text-[11.5px] text-gray-500 truncate">
                  {r.status === 'skipped' ? skipReasonLabel(r.skipReason, roles, b.audience?.titles) : r.status === 'failed' ? <span className="text-red-300/90">{r.error || 'Send failed'}</span> : r.to.map((t) => shortName(t.name, t.email)).join(', ')}
                </div>
              </div>
              <div className={`text-[11.5px] text-right tabular-nums ${m.tone}`}>{m.label}{r.status === 'sent' && r.sentAt ? <span className="block text-gray-600">{messageTime(r.sentAt)}</span> : null}</div>
            </li>
          );
        })}
        {recipients.length === 0 && <li className="px-3.5 py-6 text-center text-[12.5px] text-gray-500">No recipients recorded.</li>}
      </ul>
    </div>
  );
}

function Stat({ label, v, tone }) {
  return <div className="bg-[#0f0f16] px-4 py-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div><div className={`text-[18px] font-semibold tabular-nums mt-0.5 ${tone}`}>{v}</div></div>;
}
